import React from 'react';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { openApiFetchPost } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { Modal } from '../common/Modal/Modal';
import { EditPanelButton, EditPanelButtons } from '../elements';
import { Spinner } from '../spinners';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { PromiseReturnType } from '../utils/useReactAsync';

const fetchCopyToSimilar = async (objectId: string) => {
    const result = await openApiFetchPost('/api/objects/{objectId}/composition/copyToSimilar', { objectId }, undefined);
    return result;
};

const fetchCopyFromObject = async (sourceObjectId: string, destinationObjectId: string) => {
    const result = await openApiFetchPost(
        '/api/objects/{objectId}/composition/copyFromObject',
        { objectId: destinationObjectId },
        { sourceObjectId },
    );
    return result;
};

export function ObjectCompositionCopy(props: { objectId: string; onCopied: () => void }) {
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const translate = useTranslate();
    const [isObjectSelectVisible, setIsObjectSelectVisible] = React.useState(false);
    const [isCopyToSimularDialogVisible, setIsCopyToSimularDialogVisible] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const showObjectSelect = () => {
        setIsObjectSelectVisible(true);
    };

    const copyFromObject = async (sourceObjectId: string) => {
        setIsObjectSelectVisible(false);
        setIsLoading(true);
        await fetchCopyFromObject(sourceObjectId, props.objectId);
        setIsLoading(false);
        props.onCopied();
    };

    return (
        <>
            {isAdmin && isCustomerVersion('zxy') && (
                <EditPanelButton
                    text={translate('object.copyCompositionToSimilarObjects')}
                    onClick={() => setIsCopyToSimularDialogVisible(true)}
                    disabled={isLoading}
                    iconClass="fa-cogs"
                    small
                />
            )}
            <EditPanelButton
                text={translate('object.copyCompositionFromObject')}
                onClick={() => showObjectSelect()}
                disabled={isLoading}
                iconClass="fa-copy"
                small
            />
            {isCopyToSimularDialogVisible && <CopyToSimilarDialog objectId={props.objectId} onClose={() => setIsCopyToSimularDialogVisible(false)} />}
            {isObjectSelectVisible && (
                <ObjectSelect objectId={props.objectId} onClose={() => setIsObjectSelectVisible(false)} onSelected={copyFromObject} />
            )}
        </>
    );
}

function ObjectSelect(props: { objectId: string; onClose: () => void; onSelected: (objectId: string) => void }) {
    const translate = useTranslate();
    const [value, setValue] = React.useState('');

    const loadAvailableObjects = async (input?: string) => {
        let options: { value: string; label: string }[] = [];

        if (input && input.length >= 3) {
            const url = '/api/objects';
            const result = await fetchGet(url, { objectName: input }, ['NO_DATA_AVAILABLE']);
            const items = result.json.items as { id: string; name: string }[];
            options = items.filter((object) => object.id !== props.objectId).map((object) => ({ value: object.id, label: object.name }));
        }
        return options;
    };

    const handleSaveObject = () => {
        if (value) {
            props.onSelected(value);
        }
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
    };

    const handleCloseFormClicked = () => {
        props.onClose();
    };

    const selectStyles = {
        menu: (styles: any) => ({ ...styles, zIndex: 999 }),
    };

    return (
        <Modal id="copy-dialog" onClose={() => handleCloseFormClicked()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('object.selectCompositionFromObject')}</span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '200px' }}>
                <form method="post" action="/" id="admin-form" onSubmit={() => handleSaveObject()}>
                    <div style={{ width: '500px' }}>
                        <AsyncSelect
                            id="name"
                            name="name"
                            backspaceRemoves={true}
                            isClearable={true}
                            multi={false}
                            styles={selectStyles}
                            inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'off' }}
                            loadOptions={async (inputValue: any) => loadAvailableObjects(inputValue)}
                            onChange={(option: any) => {
                                if (option) {
                                    handleChange(option.value);
                                }
                            }}
                            valueKey="value"
                            labelKey="label"
                            placeholder={translate('common.searchItem', translate('common.object'))}
                        />
                    </div>
                </form>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.close')} onClick={() => handleCloseFormClicked()} />
                    <EditPanelButton small={true} text={translate('common.copy')} onClick={() => handleSaveObject()} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}

function CopyToSimilarDialog(props: { objectId: string; onClose: () => void }) {
    const [isLoading, setIsLoading] = React.useState(false);
    const [copyDestinationObjects, setCopyDestinationObjects] = React.useState<undefined | PromiseReturnType<typeof fetchCopyToSimilar>>();

    const handleCloseFormClicked = () => {
        props.onClose();
    };

    const copyToSimilar = async () => {
        setIsLoading(true);
        setCopyDestinationObjects(undefined);
        const result = await fetchCopyToSimilar(props.objectId);
        setCopyDestinationObjects(result);
        setIsLoading(false);
    };

    return (
        <Modal id="copy-to-all-dialog" classNames="confirm-dialog" closeOnBackdropClick={true} onClose={() => handleCloseFormClicked()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">Copy decomposition to all objects</span>
                </h3>
            </Modal.Title>
            <Modal.Content isScrollBarVisible={true} style={{ height: '150px' }}>
                {!copyDestinationObjects && (
                    <div>
                        This operation copies the composition from this object to all other objects of the same type and owner that do not yet have a
                        decomposition. <br /> <br />
                        <strong>Note: This operation cannot be undone.</strong>
                    </div>
                )}
                {copyDestinationObjects && copyDestinationObjects.length === 0 && (
                    <>
                        <div>No similar objects without a composition found.</div>
                    </>
                )}
                {copyDestinationObjects && copyDestinationObjects.length > 0 && (
                    <>
                        <div>Copied the composition to these objects:</div>
                        {copyDestinationObjects.map((copyDestinationObject) => (
                            <div key={copyDestinationObject.id}>
                                <Link to={`/objects/${copyDestinationObject.id}`}>{copyDestinationObject.name}</Link>
                            </div>
                        ))}
                    </>
                )}
                {isLoading && (
                    <div>
                        <Spinner />
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButton small={true} text="Cancel" onClick={() => handleCloseFormClicked()} />
                <EditPanelButton small={true} text="Start copying to objects" disabled={isLoading} onClick={async () => copyToSimilar()} danger />
            </Modal.Actions>
        </Modal>
    );
}
